import { Injectable, inject, signal, computed } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  GuideTourConfig,
  GuideTourState,
  GuideTourStep,
  GuideTourAction,
  GUIDE_TOUR_STORAGE_KEY,
  GUIDE_TOUR_QUERY_PARAM,
  GUIDE_TOUR_QUERY_VALUE,
  OPEN_CSV_DIALOG_PARAM,
} from '../models/guide-tour.models';
import { environment } from '../../../environments/environment';

/**
 * Service for managing the Guide Tour state and navigation.
 * Handles URL-based opening, step navigation, and persistence.
 */
@Injectable({
  providedIn: 'root',
})
export class GuideTourService {
  private readonly router = inject(Router);

  /**
   * Default configuration for the Guide Tour.
   * Steps are configured based on Figma design specifications.
   */
  private readonly config: GuideTourConfig = {
    mainTitle: "Let's take a quick tour to get you started.",
    totalSteps: 3,
    steps: [
      {
        stepNumber: 1,
        title: "Step 1: Let's add your users",
        description:
          'To get started, add users to your school. You can invite them one by one or upload a CSV file to add multiple users at once.',
        illustrationSrc: 'assets/icons/step-1-illustration.svg',
        actions: [
          {
            label: 'Upload users via CSV',
            iconSrc: 'assets/icons/csv-blue.svg',
            route: '/user-management',
            closeOnClick: true,
            actionType: 'navigate-with-action',
            queryParams: { [OPEN_CSV_DIALOG_PARAM]: 'true' },
            variant: 'secondary',
          },
          {
            label: 'Add users manually',
            iconSrc: 'assets/icons/plus-mini.svg',
            route: '/create-user',
            closeOnClick: true,
            actionType: 'navigate',
            variant: 'primary',
          },
        ],
      },
      {
        stepNumber: 2,
        title: 'Step 2: Enroll users in courses',
        description: 'Get your team learning quickly by enrolling them in courses with just a few clicks.',
        illustrationSrc: 'assets/icons/step-2-illustration.svg',
        actions: [
          {
            // TLL renames "Course Catalog" -> "Course Library" across the learner-facing UI
            // (see menu-items.ts); keep the tour's button label consistent with the nav.
            label: environment.brand === 'tll' ? 'Go to course library' : 'Go to course catalog',
            route: '/course-catalog',
            closeOnClick: true,
            actionType: 'navigate',
            variant: 'primary',
          },
        ],
      },
      {
        stepNumber: 3,
        title: "Step 3: You're in control",
        description: 'Manage your group, update details, payment methods and adjust settings whenever needed.',
        illustrationSrc: 'assets/icons/step-3-illustration.svg',
        actions: [
          {
            label: 'Go to group details',
            route: '/groups',
            closeOnClick: true,
            actionType: 'navigate',
            variant: 'primary',
          },
        ],
      },
    ],
  };

  /** Signal for modal visibility */
  private readonly _isOpen = signal<boolean>(false);

  /** Signal for current step (1-based index) */
  private readonly _currentStep = signal<number>(1);

  /** Signal indicating if user is a GroupAdmin */
  private readonly _isGroupAdmin = signal<boolean>(false);

  /** Signal indicating if currently in admin mode */
  private readonly _isAdminMode = signal<boolean>(false);

  /** Public readonly signal for modal visibility */
  public readonly isOpen = this._isOpen.asReadonly();

  /** Public readonly signal for current step */
  public readonly currentStep = this._currentStep.asReadonly();

  /** Observable for isOpen state (useful for components using async pipe) */
  public readonly isOpen$ = toObservable(this._isOpen);

  /** Computed signal for current step data */
  public readonly currentStepData = computed<GuideTourStep | null>(() => {
    const stepIndex = this._currentStep() - 1;
    const steps = this.config.steps;
    if (stepIndex >= 0 && stepIndex < steps.length) {
      return steps[stepIndex];
    }
    return null;
  });

  /** Computed signal for total steps */
  public readonly totalSteps = computed(() => this.config.totalSteps);

  /** Computed signal for main title */
  public readonly mainTitle = computed(() => this.config.mainTitle);

  /** Computed signal checking if we can go to previous step */
  public readonly canGoPrevious = computed(() => this._currentStep() > 1);

  /** Computed signal checking if we can go to next step */
  public readonly canGoNext = computed(() => this._currentStep() < this.config.totalSteps);

  /** Computed signal checking if current step is the last one */
  public readonly isLastStep = computed(() => this._currentStep() === this.config.totalSteps);

  /**
   * Computed signal for whether the guide tour button should be visible.
   * Shows only for GroupAdmin users in admin mode.
   */
  public readonly isButtonVisible = computed(() => {
    const isGroupAdmin = this._isGroupAdmin();
    const isAdminMode = this._isAdminMode();
    const isModalOpen = this._isOpen();

    return isGroupAdmin && isAdminMode && !isModalOpen;
  });

  constructor() {
    this.loadState();
  }

  /**
   * Opens the Guide Tour modal at a specific step.
   * @param step - The step number to start at (1-based, defaults to 1)
   */
  public open(step: number = 1): void {
    const validStep = Math.max(1, Math.min(step, this.config.totalSteps));
    this._currentStep.set(validStep);
    this._isOpen.set(true);
    this.updateUrlWithGuideTour(true);
    this.recordFirstShown();
  }

  /**
   * Closes the Guide Tour modal.
   * @param markDismissed - Whether to mark the tour as dismissed (defaults to false)
   */
  public close(markDismissed: boolean = false): void {
    this._isOpen.set(false);
    this.updateUrlWithGuideTour(false);

    if (markDismissed) {
      this.markDismissed();
    }
  }

  /**
   * Navigates to the next step.
   */
  public nextStep(): void {
    if (this.canGoNext()) {
      this._currentStep.update((step) => step + 1);
    }
  }

  /**
   * Navigates to the previous step.
   */
  public previousStep(): void {
    if (this.canGoPrevious()) {
      this._currentStep.update((step) => step - 1);
    }
  }

  /**
   * Sets the current step directly.
   * @param step - The step number to navigate to (1-based)
   */
  public goToStep(step: number): void {
    const validStep = Math.max(1, Math.min(step, this.config.totalSteps));
    this._currentStep.set(validStep);
  }

  /**
   * Handles action button click - navigates with optional query params.
   * Closes modal directly without URL manipulation to avoid navigation conflicts.
   * @param action - The action configuration
   */
  public handleActionClick(action: GuideTourAction): void {
    // Close modal by setting signal directly (avoid URL manipulation since we're navigating)
    if (action.closeOnClick) {
      this._isOpen.set(false);
    }

    // Build query params, explicitly removing guideTour param to prevent reopening
    const queryParams: Record<string, string | null> = {
      ...(action.queryParams || {}),
      [GUIDE_TOUR_QUERY_PARAM]: null, // Explicitly remove guideTour param
    };

    // Navigate with merged query params
    this.router.navigate([action.route], {
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Sets whether the current user is a GroupAdmin.
   * @param isGroupAdmin - Whether user is GroupAdmin role
   */
  public setGroupAdmin(isGroupAdmin: boolean): void {
    this._isGroupAdmin.set(isGroupAdmin);
  }

  /**
   * Sets whether the app is currently in admin mode.
   * @param isAdminMode - Whether currently in admin mode (not learner mode)
   */
  public setAdminMode(isAdminMode: boolean): void {
    this._isAdminMode.set(isAdminMode);
  }

  /**
   * Checks URL query params and opens guide tour if parameter is present.
   * Call this in app initialization or route guards.
   * @param route - The activated route to check for query params
   */
  public checkUrlAndOpen(route: ActivatedRoute): void {
    const guideTourParam = route.snapshot.queryParamMap.get(GUIDE_TOUR_QUERY_PARAM);
    if (guideTourParam === GUIDE_TOUR_QUERY_VALUE) {
      if (this.shouldShowTour()) {
        this.open(1);
      }
    }
  }

  /**
   * Determines if the guide tour should be shown to the user.
   * Returns true if user is GroupAdmin, in admin mode, and hasn't completed/dismissed.
   */
  public shouldShowTour(): boolean {
    const state = this.getState();
    return this._isGroupAdmin() && this._isAdminMode() && !state.isCompleted && !state.isDismissed;
  }

  /**
   * Marks the tour as completed and saves state.
   */
  public markCompleted(): void {
    const state = this.getState();
    state.isCompleted = true;
    state.completedAt = new Date().toISOString();
    this.saveState(state);
  }

  /**
   * Marks the tour as dismissed and saves state.
   */
  public markDismissed(): void {
    const state = this.getState();
    state.isDismissed = true;
    this.saveState(state);
  }

  /**
   * Resets the tour state (for testing or re-showing).
   */
  public resetState(): void {
    const initialState: GuideTourState = {
      isCompleted: false,
      isDismissed: false,
      currentStep: 1,
    };
    this.saveState(initialState);
    this._currentStep.set(1);
  }

  /**
   * Gets the complete guide tour configuration.
   */
  public getConfig(): GuideTourConfig {
    return this.config;
  }

  /**
   * Loads state from local storage.
   */
  private loadState(): void {
    const state = this.getState();
    this._currentStep.set(state.currentStep || 1);
  }

  /**
   * Gets the current state from local storage.
   */
  private getState(): GuideTourState {
    try {
      const stored = localStorage.getItem(GUIDE_TOUR_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as GuideTourState;
      }
    } catch (error) {}

    return {
      isCompleted: false,
      isDismissed: false,
      currentStep: 1,
    };
  }

  /**
   * Saves state to local storage.
   */
  private saveState(state: GuideTourState): void {
    try {
      localStorage.setItem(GUIDE_TOUR_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {}
  }

  /**
   * Records when the tour was first shown.
   */
  private recordFirstShown(): void {
    const state = this.getState();
    if (!state.firstShownAt) {
      state.firstShownAt = new Date().toISOString();
      this.saveState(state);
    }
  }

  /**
   * Updates the URL with or without the guide tour query parameter.
   */
  private updateUrlWithGuideTour(add: boolean): void {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);

    if (add) {
      urlTree.queryParams[GUIDE_TOUR_QUERY_PARAM] = GUIDE_TOUR_QUERY_VALUE;
    } else {
      delete urlTree.queryParams[GUIDE_TOUR_QUERY_PARAM];
    }

    this.router.navigateByUrl(urlTree, { replaceUrl: true });
  }
}
